/**
 * The party's retainers.
 *
 * **Every rule shown here is served, never computed.** The Charisma cap on how
 * many a character may retain, the loyalty rating, the printed list of what
 * calls for a check — all of it arrives from `GET /api/campaigns/:id/henchmen`,
 * because each one is a printed table and a copy kept here would be a second
 * source that still renders as a confident number when it goes stale.
 *
 * Two audiences in one panel. **Everyone** sees who is retained and how loyal
 * they are: a henchman fights beside the party and their loyalty is what decides
 * whether they still will next round. **A patron's own player** additionally
 * hires for them, and rolls the checks — `directableCharacter` is the one
 * definition of whose retinue is yours to act for.
 *
 * Takes its data as props and asks for it through a callback, like every other
 * panel here: zustand serves the *initial* state to a server render, so a
 * component that subscribed could only ever be tested empty.
 */

import { optionLabel, rerollNote } from '../characters/options.js';
import { titleLabel } from '../labels.js';
import { useState } from 'react';

import { api, ApiError } from '../api.js';
import type { CharacterOptions, Scores } from '../characters/options.js';

export interface HenchmanView {
  readonly id: string;
  readonly name: string;
  readonly race: string;
  readonly alignment: string;
  readonly loyalty: number | null;
  readonly hitPointsCurrent: number;
  readonly hitPointsMax: number;
  readonly deathStatus: string;
  /** The table has been asked to make this henchman a player character. */
  readonly promotionOffered?: boolean;
  readonly classes: readonly { readonly class: string; readonly level: number }[];
}

export interface PatronView {
  readonly characterId: string;
  readonly name: string;
  readonly slots: { readonly maximum: number; readonly used: number; readonly free: number };
  readonly henchmen: readonly HenchmanView[];
}

export interface HenchmenView {
  readonly patrons: readonly PatronView[];
  /** The book's own list of when a check is called for. */
  readonly checkTriggers: readonly string[];
  /**
   * The printed morale modifiers, with their values.
   *
   * Served rather than kept here: they are a rules table, and a client copy
   * would still render a confident number after the table changed. Until
   * 2026-08-15 the route accepted these names and no client sent any, so every
   * check in the product was thrown against an unmodified rating.
   */
  readonly modifiers?: readonly {
    readonly name: string;
    readonly value: number;
    /** The printed row label (Table 8.12) and, where the book prints one, its footnote. */
    readonly label?: string;
    readonly note?: string;
  }[];
}

/**
 * How the list is laid out for a person: the table prints 25 rows in one run, which reads as a heap.
 * The grouping is this application's arrangement of them, not the book's; every row is still the
 * book's own words and number. A key not named here still shows, under "Other".
 */
const MODIFIER_GROUPS: readonly { readonly title: string; readonly names: readonly string[] }[] = [
  {
    title: 'How the fight is going',
    names: [
      'creatureWasSurprised',
      'abandonedByAllies',
      'creatureLost25PctHP',
      'creatureLost50PctHP',
      'mostPowerfulAllyDefeated',
      'noOpponentsDefeated',
      'outnumbered3to1',
      'outnumberingOpponents3to1',
      'unableToAffectOpponent',
      'eachAdditionalCheckThisRound',
    ],
  },
  {
    title: 'Who they are fighting, and where',
    names: ['creatureFightingHatedEnemy', 'fightingSpellCasters', 'defendingLair', 'terrainAdvantage'],
  },
  {
    title: 'The creature itself',
    names: [
      'creatureIsLawful',
      'creatureIsChaotic',
      'halfHDOrLess',
      'lessThan1HDMoreThanHalf',
      'hd4to8',
      'hd9to14',
      'hd15Plus',
    ],
  },
  {
    title: 'Their employer and companions',
    names: ['npcTreatedExceptionallyWell', 'npcTreatedPoorly', 'leaderOfDifferentAlignment', 'spellCasterAsAlly'],
  },
];

/**
 * The served modifiers, split into `MODIFIER_GROUPS` in that order, with anything not named in any group
 * kept at the end so no row the server sends can go missing from the screen.
 */
export function modifierGroups<T extends { readonly name: string }>(
  modifiers: readonly T[],
): readonly { readonly title: string; readonly modifiers: readonly T[] }[] {
  const named = new Set(MODIFIER_GROUPS.flatMap((group) => group.names));
  const groups = MODIFIER_GROUPS.map((group) => ({
    title: group.title,
    modifiers: group.names.flatMap((name) => modifiers.filter((m) => m.name === name)),
  }));
  const other = modifiers.filter((m) => !named.has(m.name));
  return [...groups, ...(other.length > 0 ? [{ title: 'Other', modifiers: other }] : [])].filter(
    (group) => group.modifiers.length > 0,
  );
}

/** The row's printed label, falling back to the key made readable if an older server sent none. */
function modifierLabel(modifier: { readonly name: string; readonly label?: string }): string {
  return modifier.label ?? titleLabel(modifier.name);
}


/**
 * `scores` as rolled, remapped onto the full ability names the server's
 * `parseStats` requires on the way in.
 *
 * ⚠ **Not a no-op.** `options.abilities` — and so `Scores`' own keys — are
 * `ABILITY_KEYS` (`str`/`dex`/`con`/`int`/`wis`/`cha`): convenient for a
 * `.slice(0, 3)`-style display, but `parseStats` recognises only
 * `strength`/`dexterity`/…, and refuses anything else as a missing score
 * without naming which one. `NewCharacterPage.tsx`'s own submit does this
 * identical remap for an ordinary character; extracted here (rather than
 * left inline in the submit handler) because a static render never fires a
 * click, so the only way to prove the keys actually change is to call this
 * function directly.
 */
export function henchmanStats(scores: Scores): {
  readonly strength: number;
  readonly dexterity: number;
  readonly constitution: number;
  readonly intelligence: number;
  readonly wisdom: number;
  readonly charisma: number;
} {
  return {
    strength: scores['str'] ?? 0,
    dexterity: scores['dex'] ?? 0,
    constitution: scores['con'] ?? 0,
    intelligence: scores['int'] ?? 0,
    wisdom: scores['wis'] ?? 0,
    charisma: scores['cha'] ?? 0,
  };
}

export function HenchmenPanel({
  campaignId,
  view,
  myCharacterId,
  options,
  onCommand,
  onChanged,
}: {
  readonly campaignId: string;
  readonly view: HenchmenView | null;
  readonly myCharacterId: string | null;
  /** Races, classes and alignments, as the server publishes them. */
  readonly options: CharacterOptions | null;
  /** Socket commands — promotion is an offer and an agreement, not a REST call. */
  readonly onCommand?: (event: string, payload?: Record<string, unknown>) => void;
  readonly onChanged: () => void;
}) {
  /**
   * Which printed modifiers apply to the next check.
   *
   * Shared across the panel rather than per henchman: a morale check is thrown
   * for a *situation* — the party is outnumbered, their best fighter just went
   * down — and that situation is the same for everyone in it.
   */
  const [chosenModifiers, setChosenModifiers] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [hiringFor, setHiringFor] = useState<string | null>(null);

  // Nothing at all until somebody is seated. An empty panel headed "Henchmen"
  // on a table that has never hired anyone is a control looking for a purpose.
  if (!view || view.patrons.length === 0) return null;
  const retained = view.patrons.reduce((sum, patron) => sum + patron.henchmen.length, 0);
  // ⚠ **No longer gated on a role.** Retainers are the *patron's*, and the
  // controls below are the patron's and the table's respectively.
  //
  // ⚠ But an empty panel still has to reach the person who can fill it: a
  // patron with no retainers *yet* is exactly who needs the hire button, and
  // hiding it from them is how the old gate happened to be right. The
  // condition is ownership now, not a role.
  const iAmAPatron = view.patrons.some((p) => p.characterId === myCharacterId);
  if (retained === 0 && !iAmAPatron) return null;

  const run = async (what: () => Promise<string | null>) => {
    setBusy(true);
    setNotice(null);
    try {
      setNotice(await what());
      onChanged();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : 'that did not work');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Henchmen"
      className="rounded-md border border-neutral-300 p-3 text-sm dark:border-neutral-700"
    >
      <h2 className="font-semibold">Henchmen</h2>

      {/* **The situation, which only a person at the table can know.** 22 of the
          table's 25 modifiers were unreachable from the product: the route
          validated them and no client ever sent one, so every check was thrown
          against an unmodified rating. The values come from the server because
          they are a printed table. */}
      {(view.modifiers ?? []).length > 0 && (
        <details className="rounded-md border border-neutral-200 p-2 text-xs dark:border-neutral-800">
          <summary className="cursor-pointer text-neutral-500 dark:text-neutral-400">
            What is happening right now?{chosenModifiers.length > 0 ? ` — ${chosenModifiers.length} ticked` : ''}
          </summary>
          <p className="mt-1 text-neutral-600 dark:text-neutral-400">
            Used by the <strong>Loyalty check</strong> button below. When a henchman&rsquo;s loyalty is
            tested, tick everything on this list that is true at the moment. Each ticked line adds its number to
            (or takes it from) the henchman&rsquo;s rating for that check; leave them all unticked for an ordinary
            check. These are the book&rsquo;s Table 8.12 morale modifiers.
          </p>
          {modifierGroups(view.modifiers ?? []).map((group) => (
            <div key={group.title} className="mt-2">
              <p className="font-medium text-neutral-700 dark:text-neutral-300">{group.title}</p>
              <ul className="space-y-0.5">
                {group.modifiers.map((modifier) => (
                  <li key={modifier.name}>
                    <label className="flex items-center gap-1" title={modifier.note}>
                      <input
                        type="checkbox"
                        checked={chosenModifiers.includes(modifier.name)}
                        onChange={(e) =>
                          setChosenModifiers((current) =>
                            e.target.checked
                              ? [...current, modifier.name]
                              : current.filter((name) => name !== modifier.name),
                          )
                        }
                      />
                      <span>
                        {modifierLabel(modifier)}{' '}
                        <span className="text-neutral-500 dark:text-neutral-400">
                          {modifier.value >= 0 ? `+${modifier.value}` : modifier.value}
                        </span>
                        {modifier.note && (
                          <span className="ml-1 text-neutral-500 dark:text-neutral-400">({modifier.note})</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </details>
      )}

      {view.patrons.map((patron) => (
        <div key={patron.characterId} className="mt-2">
          <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {patron.name} — {patron.slots.used} of {patron.slots.maximum}
          </p>

          {patron.henchmen.length === 0 && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">No retainers.</p>
          )}

          <ul className="space-y-1">
            {patron.henchmen.map((henchman) => (
              <li key={henchman.id} className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium">{henchman.name}</span>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {henchman.classes.map((c) => `${c.class} ${c.level}`).join(' / ')} ·{' '}
                  {henchman.hitPointsCurrent}/{henchman.hitPointsMax} hp
                  {/* Loyalty is the number a 2d10 is thrown against, so it is
                      shown as such rather than as a bar or a word: a player who
                      can see 15 knows what a check costs them. */}
                  {henchman.loyalty !== null && ` · loyalty ${henchman.loyalty}`}
                </span>

                {/* ⚠ **The patron's, matching the server.** The book's own
                    triggers for a loyalty check are things a patron does —
                    offering a bribe, ordering a charge spent — and the route
                    now refuses anyone else, so offering it to the table would
                    be a button whose only outcome is a refusal. */}
                {patron.characterId === myCharacterId && (
                  <button
                    type="button"
                    disabled={busy}
                    className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                    onClick={() =>
                      void run(async () => {
                        const { check } = await api.post<{
                          check: { roll: number; rating: number; held: boolean };
                        }>(
                          `/api/campaigns/${campaignId}/henchmen/${henchman.id}/loyalty-check`,
                          // **The situation the check is thrown in.** The route
                          // has always taken these and nothing ever sent one.
                          { situational: [...chosenModifiers] },
                        );
                        // The transcript carries the outcome for the room; this
                        // is only the presser's acknowledgement.
                        return `${henchman.name}: ${check.roll} against ${check.rating} — ${
                          check.held ? 'holds' : 'breaks'
                        }`;
                      })
                    }
                  >
                    Loyalty check
                  </button>
                )}

                {/* **An offer, and an agreement.** The book has the GM present
                    the option and the player agree, so these are two controls
                    for two people: the table offers, and only the patron's
                    own player can accept. Accepting spends the Charisma slot
                    permanently — even if the promoted character later dies — so
                    the button says so rather than reading as a free upgrade. */}
                {/* ⚠ **There is no "offer" button here, deliberately.** One of
                    the four judgements that became a table vote on 2026-08-24,
                    so it is proposed from "Put something to the table" on the
                    campaign page — which carries the guards this panel has not:
                    one open proposal per table, and a roster picker to name the
                    subject. A button here emitted `host:offer_promotion`, which
                    no handler has ever listened for and which is not in the
                    contract, so pressing it did nothing at all: the same
                    bare-string shape that hid four dead controls in
                    `TablePanel`. `contract.test.ts` now greps every
                    `onCommand` name against `ClientToServerEvents`. */}
                {henchman.promotionOffered && patron.characterId === myCharacterId && (
                  <button
                    type="button"
                    title="Their slot in your Charisma limit stays used permanently, even if they later die."
                    className="rounded-md border border-amber-400 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400"
                    onClick={() =>
                      onCommand?.('player:accept_promotion', { characterId: henchman.id })
                    }
                  >
                    Accept — they become yours
                  </button>
                )}
                {henchman.promotionOffered && patron.characterId !== myCharacterId && (
                  <span className="text-xs text-amber-700 dark:text-amber-400">
                    offered a seat
                  </span>
                )}

                {/* **The sheet, so a retainer can be equipped.** Hiring set
                    their kit and nothing could change it afterwards: a hired
                    henchman could never be handed a sword, given armour, or
                    take a share of the loot. Only their own patron — the server
                    answers 404 to anybody else, because whether a given id
                    exists is not a stranger's business. */}
                {patron.characterId === myCharacterId && (
                  <a
                    href={`/characters/${henchman.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                  >
                    Sheet ↗
                  </a>
                )}

                {patron.characterId === myCharacterId && (
                  <button
                    type="button"
                    disabled={busy}
                    className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                    onClick={() =>
                      void run(async () => {
                        await api.delete(
                          `/api/campaigns/${campaignId}/henchmen/${henchman.id}?reason=dismissed`,
                        );
                        return `${henchman.name} is released from service.`;
                      })
                    }
                  >
                    Release
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* ⚠ **Hiring is the patron's player's** (2026-08-24), so it is
              theirs to press — it was gated on a role that no longer exists. */}
          {patron.characterId === myCharacterId && patron.slots.free > 0 && (
            <button
              type="button"
              className="mt-1 text-xs underline"
              onClick={() =>
                setHiringFor(hiringFor === patron.characterId ? null : patron.characterId)
              }
            >
              {hiringFor === patron.characterId ? 'Cancel' : `Hire for ${patron.name}`}
            </button>
          )}
          {patron.characterId === myCharacterId && patron.slots.free === 0 && (
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Charisma {patron.slots.maximum === 0 ? 'supports no retainers' : 'allows no more'}.
            </p>
          )}

          {hiringFor === patron.characterId && options && (
            <HireForm
              campaignId={campaignId}
              patron={patron}
              options={options}
              onDone={(message) => {
                setHiringFor(null);
                setNotice(message);
                onChanged();
              }}
            />
          )}
        </div>
      ))}

      {notice && <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">{notice}</p>}

      {/* The printed triggers, shown to everybody: a loyalty check is the
          patron's to call and the reasons are the book's, not a secret. */}
      {(
        <details className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
          <summary className="cursor-pointer">When to call for a check</summary>
          <ul className="ml-4 list-disc">
            {view.checkTriggers.map((trigger) => (
              <li key={trigger}>{trigger}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/**
 * Hiring one.
 *
 * **The scores are rolled by the server**, through the same `/api/character-rolls`
 * a player character is made with — a client that rolled them would be a client
 * that chooses its own results, and the henchman goes through the identical
 * validator on the way in. So this form collects who they are and asks for the
 * dice; it computes nothing.
 */
export function HireForm({
  campaignId,
  patron,
  options,
  onDone,
}: {
  readonly campaignId: string;
  readonly patron: PatronView;
  readonly options: CharacterOptions;
  readonly onDone: (message: string) => void;
}) {
  const rollMethod = options.generationMethods.find((m) => m.kind !== 'points');
  const [name, setName] = useState('');
  const [race, setRace] = useState(options.races[0]?.race ?? '');
  const [characterClass, setCharacterClass] = useState(options.classes[0]?.class ?? '');
  const [alignment, setAlignment] = useState(options.alignments[0] ?? '');
  const [scores, setScores] = useState<Scores | null>(null);
  const [rollNote, setRollNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Scores rolled for one race and class say nothing about another, so choosing again clears them. */
  const forgetRoll = () => {
    setScores(null);
    setRollNote(null);
  };

  const rollScores = async () => {
    if (!rollMethod) return;
    setBusy(true);
    setError(null);
    try {
      // Rolled for the race and class chosen: the server throws away sets that could not qualify (this table's
      // rule, not the book's) and says how many.
      const body = await api.post<{ scores: number[]; rerolls?: number }>('/api/character-rolls', {
        method: rollMethod.key,
        race,
        classes: [characterClass],
      });
      setScores(Object.fromEntries(options.abilities.map((a, i) => [a, body.scores[i] ?? 0])));
      setRollNote(body.rerolls === undefined ? null : rerollNote(body.rerolls, race, characterClass));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'the dice did not answer');
    } finally {
      setBusy(false);
    }
  };

  const hire = async () => {
    if (!scores) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/campaigns/${campaignId}/henchmen`, {
        patronCharacterId: patron.characterId,
        name,
        race,
        alignment,
        // **The scores as rolled**, not adjusted: a race is qualified for on
        // what was rolled and the server applies its own adjustment on the way
        // in. Adjusting here would qualify a dwarf on a Constitution it only
        // has because it is a dwarf. See `henchmanStats`'s own docstring for
        // why this cannot be `scores` itself.
        stats: henchmanStats(scores),
        classes: [{ class: characterClass, level: 1 }],
      });
      onDone(`${name} enters ${patron.name}'s service.`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'that hire was refused');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 space-y-2 rounded-md border border-neutral-300 p-2 dark:border-neutral-700">
      <label className="block text-xs">
        Name
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mt-0.5 w-full rounded-md border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Race"
          value={race}
          onChange={(event) => {
            setRace(event.target.value);
            forgetRoll();
          }}
          className="rounded-md border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
        >
          {options.races.map((option) => (
            <option key={option.race} value={option.race}>
              {optionLabel(option.race)}
            </option>
          ))}
        </select>
        <select
          aria-label="Class"
          value={characterClass}
          onChange={(event) => {
            setCharacterClass(event.target.value);
            forgetRoll();
          }}
          className="rounded-md border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
        >
          {options.classes.map((option) => (
            <option key={option.class} value={option.class}>
              {optionLabel(option.class)}
            </option>
          ))}
        </select>
        <select
          aria-label="Alignment"
          value={alignment}
          onChange={(event) => setAlignment(event.target.value)}
          className="rounded-md border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
        >
          {options.alignments.map((option) => (
            <option key={option} value={option}>
              {optionLabel(option)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || !rollMethod}
          onClick={() => void rollScores()}
          className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
        >
          Roll scores
        </button>
        {scores && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {options.abilities.map((ability) => `${ability.slice(0, 3)} ${scores[ability]}`).join(' · ')}
          </span>
        )}
        {rollNote && (
          <span role="status" className="basis-full text-xs text-neutral-500 dark:text-neutral-400">
            {rollNote}
          </span>
        )}
      </div>

      {/* Disabled until the dice have been thrown: there is nothing to submit
          without them, and a hire button that answers "stats is required" is a
          worse way to say so. */}
      <button
        type="button"
        disabled={busy || !scores || name.trim() === ''}
        onClick={() => void hire()}
        className="rounded-md border border-neutral-400 px-2 py-0.5 text-xs disabled:opacity-50 dark:border-neutral-600"
      >
        Hire
      </button>

      {/* The server's own words. A qualification refusal names the ability and
          the number, which is the only useful thing to say. */}
      {error && <p className="text-xs text-rose-700 dark:text-rose-400">{error}</p>}
    </div>
  );
}
